import { bindServices } from '@/services/api';

const api = bindServices('processPortalConfig');
import { getParamsAddConfigProcessPortal, getParamsSelectedConfigPortal } from './utils';
import { ConfigPermission, ConfigMenu } from './data';

const MENU_EXPAND_KEYS = [
  'group',
  'customList',
  'dynamicEntity',
  'report',
  'process',
  'parent',
  'processPortalMenuGroup',
  'name',
];

export const initialPagination = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const delay = (timeout) => {
  return new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
};

const compareMenuByOrder = (a, b) => (a?.order ?? 0) - (b?.order ?? 0);

const Model = {
  namespace: 'processPortalConfig',

  state: {
    pagination: initialPagination,
    isOpenFormAddConfig: false,
    dataTable: [],
    configCache: {}, // Cache to store configuration data by idConfig
    userListPermission: null, // null means never loaded, [] means loaded but empty
    positionListPermission: null,
    userGroupListPermission: null,
    areaListPermission: null,
    fetchingPermissionList: null, // name of the permission list currently fetching (per tab)
    permissionsCache: {}, // Cache for permissions data
    listMenuGroups: null, // null means never loaded, [] means loaded but empty
    listMenuItems: null,
    menuCache: {}, // Cache for menu data
  },

  effects: {
    *fetchList({ payload: { idConfig, idProcessPortal, readPriority = false } }, { call, put }) {
      try {
        const { paramsTable } = getParamsSelectedConfigPortal({
          idProcessPortal,
          id: idConfig,
        });
        const dataTable = yield call(api.getAll, { ...paramsTable, readPriority });
        const processedData = dataTable.map((item) => ({
          ...item,
          name: item.displayName || item.name || item.title || item.fullName,
        }));

        yield put({
          type: 'queryFetchList',
          payload: {
            id: idConfig,
            idProcessPortal,
            dataTable: processedData,
          },
        });
      } catch (error) {}
    },
    *fetchListForCache({ payload: { idConfig, idProcessPortal } }, { call, put }) {
      try {
        const { paramsTable } = getParamsSelectedConfigPortal({
          idProcessPortal,
          id: idConfig,
        });
        const dataTable = yield call(api.getAll, paramsTable);
        const cacheKey = `${idProcessPortal}_${idConfig}`;
        let processedData;

        if (idConfig === 'config-entity') {
          processedData = dataTable.map((item) => ({
            ...item,
            name: item.displayName || item.name || item.title || item.fullName,
          }));
        } else {
          processedData = dataTable.map((item) => ({
            ...item,
            name: item.name || item.title || item.fullName || item.displayName,
          }));
        }

        yield put({
          type: 'cacheConfigData',
          payload: {
            key: cacheKey,
            data: processedData,
          },
        });
      } catch (error) {}
    },
    *toggleConfigDetail({ payload: { id, state, idProcessPortal } }, { call, put, select }) {
      try {
        const currentState = yield select((state) => state.processPortalConfig);
        const cacheKey = `${idProcessPortal}_${id}`;

        // If opening a configuration
        if (state) {
          // If opening the same configuration that's already open, don't make request
          if (currentState.isOpenConfig && currentState.idConfig === id) {
            // Use cached data if available
            if (currentState.configCache[cacheKey]) {
              yield put({
                type: 'queryToggleConfigDetail',
                payload: {
                  id,
                  state: true,
                  dataTable: currentState.configCache[cacheKey],
                },
              });
              return;
            }
          }

          // If we have cached data for this configuration, use it and don't make request
          if (currentState.configCache[cacheKey] !== undefined) {
            yield put({
              type: 'queryToggleConfigDetail',
              payload: {
                id,
                state: true,
                dataTable: currentState.configCache[cacheKey],
              },
            });
            return;
          }

          // No cache available, show skeleton and fetch data
          yield put({
            type: 'queryToggleConfigDetail',
            payload: {
              id,
              state: true,
              dataTable: [],
            },
          });

          // Permissions and Menu have their own loading system by tabs, don't fetch here
          if (id === ConfigPermission.id || id === ConfigMenu.id) {
            return;
          }

          const { paramsTable } = getParamsSelectedConfigPortal({
            idProcessPortal,
            id,
          });
          const dataTable = yield call(api.getAll, paramsTable);

          // Cache the results
          const processedData = dataTable.map((item) => ({
            ...item,
            name: item.displayName || item.name || item.title || item.fullName,
          }));

          yield put({
            type: 'cacheConfigData',
            payload: {
              key: cacheKey,
              data: processedData,
            },
          });

          yield put({
            type: 'queryToggleConfigDetail',
            payload: {
              id,
              state,
              dataTable: processedData,
            },
          });
        } else {
          // Closing configuration - only clear dataTable, keep cache
          yield put({
            type: 'queryToggleConfigDetail',
            payload: {
              id,
              state,
              dataTable: [],
            },
          });
        }
      } catch (error) {}
    },
    *toggleAddToConfig({ payload }, { put }) {
      yield put({
        type: 'queryToggleAddToConfig',
        payload,
      });
    },
    *hideAllForms({ payload = 'hideForms' }, { put }) {
      yield put({ type: payload });
    },
    *addConfigProcessPortal({ payload }, { call, put }) {
      const { dataUpdate, idProcessPortal, idConfig } = payload;
      try {
        const paramsAdd = getParamsAddConfigProcessPortal({
          dataUpdate,
          idProcessPortal,
          idConfig,
        });
        yield call(api.update, paramsAdd);

        yield put({
          type: 'queryAddConfigProcessPortal',
          payload: {
            id: idConfig,
            state: false,
          },
        });

        yield put({
          type: 'global/showNotificationsForm',
          payload: {
            visible: true,
            type: 'success',
          },
        });
        payload?.onFinish();
      } catch (error) {}
    },
    *deleteItem({ id, params }, { call, put }) {
      try {
        yield put({
          type: `global/modalConfirm`,
          state: false,
          id: null,
        });

        const { idConfig, idProcessPortal, dataTable } = params;
        const dataUpdate = dataTable.filter((item) => item.id !== id);

        const paramsAdd = getParamsAddConfigProcessPortal({
          dataUpdate,
          idProcessPortal,
          idConfig,
        });

        yield call(api.update, paramsAdd);

        yield put({
          type: 'queryAddConfigProcessPortal',
          payload: {
            id: idConfig,
            state: false,
          },
        });

        yield put({
          type: 'global/showNotificationsForm',
          payload: {
            visible: true,
            type: 'success',
          },
        });

        yield put({
          type: 'fetchList',
          payload: { idConfig, idProcessPortal, readPriority: true },
        });

        yield put({
          type: 'global/showNotificationsForm',
          payload: {
            visible: false,
            type: 'success',
          },
        });
      } catch (error) {}
    },
    *addConfigItem({ payload }, { put }) {
      yield put({ type: 'addConfigItemList', payload });
    },
    *deleteConfigItem({ id }, { put }) {
      yield put({ type: 'deleteConfigItemList', payload: id });
    },
    *saveConfigItems({ payload }, { call, put, select }) {
      try {
        const { idProcessPortal, idConfig, data } = payload;
        const paramsAdd = getParamsAddConfigProcessPortal({
          dataUpdate: data,
          idProcessPortal,
          idConfig,
        });
        yield call(api.update, paramsAdd);

        // Update cache with current dataTable (which has id and name) after saving
        const cacheKey = `${idProcessPortal}_${idConfig}`;
        const dataTable = yield select((s) => s.processPortalConfig.dataTable);
        // Ensure each item has name processed correctly
        const processedData = dataTable.map((item) => ({
          ...item,
          name: item.name || item.title || item.fullName || item.displayName || item.description,
        }));
        yield put({
          type: 'cacheConfigData',
          payload: { key: cacheKey, data: processedData },
        });

        // Close section after save (same pattern as permissions / environments relation drawers).
        yield put({
          type: 'toggleConfigDetail',
          payload: { id: null, state: false, idProcessPortal },
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        yield put({
          type: 'saveSuccess',
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: false,
              visibleForm: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },
    *fetchListPermissions({ payload }, { call, put, select }) {
      const { route, params, name, id } = payload;
      let trackingFetch = false;
      try {
        const currentState = yield select((state) => state.processPortalConfig);

        // Create cache key: id_permissionType
        const permissionTypeMap = {
          userListPermission: 'user',
          positionListPermission: 'position',
          areaListPermission: 'area',
          userGroupListPermission: 'userGroup',
        };
        const permissionType = permissionTypeMap[name] || name;
        const cacheKey = `${id}_${permissionType}`;

        // Local state exists (even if empty []) — preserve it, don't refetch
        if (currentState[name] !== null && currentState[name] !== undefined) {
          return;
        }

        if (currentState.permissionsCache[cacheKey] !== undefined) {
          yield put({
            type: 'queryListPermissions',
            payload: { [name]: currentState.permissionsCache[cacheKey] },
          });
          return;
        }

        trackingFetch = true;
        yield put({ type: 'queryFetchingPermissionList', payload: name });

        const data = yield call(api.getAll, { route, params });
        const processedData = (data || []).map((item) => ({
          ...item,
          name: item.name || item.title || item.fullName || item.displayName || item.description,
          fullName: item.fullName || item.name || item.title,
        }));

        yield put({
          type: 'cachePermissionsData',
          payload: { key: cacheKey, data: processedData },
        });

        yield put({
          type: 'queryListPermissions',
          payload: { [name]: processedData },
        });
      } catch (error) {
        /*  */
      } finally {
        if (trackingFetch) {
          yield put({ type: 'queryFetchingPermissionList', payload: null });
        }
      }
    },
    *addListPermission({ payload }, { put }) {
      yield put({
        type: 'queryAddListPermission',
        payload,
      });
      // Removed cache update here - cache should only be updated after saving
    },
    *deleteItemPermission({ payload }, { put }) {
      yield put({
        type: 'queryDeleteItemPermission',
        payload,
      });
      // Removed cache update here - cache should only be updated after saving
    },
    *savePermission({ payload }, { call, put, select }) {
      try {
        const { id } = payload;
        const currentState = yield select((state) => state.processPortalConfig);

        // Before saving, ensure all permission tabs are loaded (check cache)
        // If any tab doesn't have cache, fetch it from API first
        const permissionConfigs = [
          {
            name: 'userListPermission',
            type: 'user',
            route: `processPortal/allowedUsers/${id}`,
            params: {
              select: 'id,fullName',
              expand: { position: { select: 'name' } },
              orderBy: 'fullName',
            },
          },
          {
            name: 'positionListPermission',
            type: 'position',
            route: `processPortal/allowedPositions/${id}`,
            params: {
              select: 'id,name,description',
              orderBy: 'name',
            },
          },
          {
            name: 'areaListPermission',
            type: 'area',
            route: `processPortal/allowedAreas/${id}`,
            params: {
              select: 'id,name,description',
              orderBy: 'name',
            },
          },
          {
            name: 'userGroupListPermission',
            type: 'userGroup',
            route: `processPortal/allowedUserGroups/${id}`,
            params: {
              select: 'id,name,description',
              orderBy: 'name',
            },
          },
        ];

        // Check which tabs need to be loaded (don't have cache)
        const tabsToLoad = [];
        for (const config of permissionConfigs) {
          const cacheKey = `${id}_${config.type}`;
          if (currentState.permissionsCache[cacheKey] === undefined) {
            tabsToLoad.push(config);
          }
        }

        // Load missing tabs from API
        if (tabsToLoad.length > 0) {
          for (const config of tabsToLoad) {
            const cacheKey = `${id}_${config.type}`;

            // Fetch from API
            const apiData = yield call(api.getAll, { route: config.route, params: config.params });
            const processedData = (apiData || []).map((item) => ({
              ...item,
              name:
                item.name || item.title || item.fullName || item.displayName || item.description,
              fullName: item.fullName || item.name || item.title,
            }));

            // Cache the data
            yield put({
              type: 'cachePermissionsData',
              payload: {
                key: cacheKey,
                data: processedData,
              },
            });

            // Always update local state with fetched data if it's null or empty
            // This ensures that when saving, we use the data from API (existing permissions)
            // If local state has changes (length > 0), we preserve them
            // But if local state is null or empty [], we update it with API data
            const currentLocalState = yield select(
              (state) => state.processPortalConfig[config.name],
            );
            if (
              currentLocalState === null ||
              (Array.isArray(currentLocalState) && currentLocalState.length === 0)
            ) {
              // Local state is null (never loaded) or empty (never visited this tab)
              // Update with API data to preserve existing permissions
              yield put({
                type: 'queryListPermissions',
                payload: { [config.name]: processedData },
              });
            }
            // If local state has data (length > 0), it means user made changes, so preserve it
            // Note: We need to wait for the state update to complete before saving
          }
        }

        // Now get the updated state with all tabs loaded
        const updatedState = yield select((state) => state.processPortalConfig);

        // Build the data payload using the updated state
        // This ensures we use the data from API for tabs that were just loaded
        const saveData = {
          users: (updatedState.userListPermission || []).map((l) => ({ id: l.id })),
          positions: (updatedState.positionListPermission || []).map((l) => ({ id: l.id })),
          userGroups: (updatedState.userGroupListPermission || []).map((l) => ({ id: l.id })),
          areas: (updatedState.areaListPermission || []).map((l) => ({ id: l.id })),
        };

        // Now save with all data loaded
        yield call(api.exec, {
          route: `processPortal/updatePermissions/${id}`,
          httpMethod: 'post',
          data: saveData,
        });

        // Update cache with current permission lists after successful save
        // Reuse updatedState from above (already selected after save)
        const permissionLists = {
          userListPermission: 'user',
          positionListPermission: 'position',
          areaListPermission: 'area',
          userGroupListPermission: 'userGroup',
        };

        // Update cache for each permission list
        for (const listName of Object.keys(permissionLists)) {
          const permissionType = permissionLists[listName];
          const cacheKey = `${id}_${permissionType}`;
          const currentList = updatedState[listName] || [];
          const processedData = currentList.map((item) => ({
            ...item,
            name: item.name || item.title || item.fullName || item.displayName || item.description,
          }));
          yield put({
            type: 'cachePermissionsData',
            payload: { key: cacheKey, data: processedData },
          });
        }

        // Close the permissions section after saving (same pattern as environments).
        yield put({
          type: 'toggleConfigDetail',
          payload: { id: null, state: false, idProcessPortal: id },
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: false,
              visibleForm: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },
    *fetchListMenu({ payload }, { call, put, select }) {
      try {
        const { route, params, name, idProcessPortal } = payload;
        const currentState = yield select((state) => state.processPortalConfig);

        // Create cache key: idProcessPortal_menuType
        const menuTypeMap = {
          listMenuGroups: 'group',
          listMenuItems: 'item',
        };
        const menuType = menuTypeMap[name] || name;
        const cacheKey = `${idProcessPortal}_${menuType}`;

        // If local state is null, it means never loaded - load from cache or API
        // If local state is [] (empty array), it means loaded but all items deleted - preserve it
        // If local state has data (length > 0), preserve it (local changes)
        if (currentState[name] === null) {
          // Local state never loaded, check cache first
          if (currentState.menuCache[cacheKey] !== undefined) {
            // Load from cache to state local
            yield put({
              type: 'queryListMenu',
              payload: {
                name,
                data: currentState.menuCache[cacheKey],
              },
            });
            return;
          }
          // No cache, will fetch from API below
        } else {
          // Local state exists (even if empty []), preserve it
          return;
        }

        // No cache, fetch from API
        const data = yield call(api.getAll, { route, params });
        const processedData = data.map((item) => ({
          ...item,
          name: item.name || item.title || item.fullName || item.displayName || item.description,
        }));

        // Cache the data (source of truth)
        yield put({
          type: 'cacheMenuData',
          payload: {
            key: cacheKey,
            data: processedData,
          },
        });

        // Also update state local with the fetched data
        yield put({
          type: 'queryListMenu',
          payload: {
            name,
            data: processedData,
          },
        });
      } catch (error) {
        /*  */
      }
    },
    *addListMenu({ payload }, { put }) {
      yield put({
        type: 'queryAddListMenu',
        payload,
      });
    },
    *updateListMenu({ payload }, { put }) {
      yield put({
        type: 'queryUpdateListMenu',
        payload,
      });
    },
    *deleteItemMenu({ payload }, { put }) {
      yield put({
        type: 'queryDeleteItemMenu',
        payload,
      });
    },
    *saveMenu({ payload }, { call, put, select }) {
      try {
        const { id } = payload;
        const currentState = yield select((state) => state.processPortalConfig);

        const menuConfigs = [
          {
            name: 'listMenuGroups',
            type: 'group',
            route: `processPortal/menuGroups/${id}`,
            params: {
              select: 'id,title,order',
              orderBy: 'order',
            },
          },
          {
            name: 'listMenuItems',
            type: 'item',
            route: `processPortal/menuItems/${id}`,
            params: {
              select:
                'id,title,order,type,icon,processPortalMenuGroupId,parentId,customListId,dynamicEntityId,reportId,processId,link,data,default',
              orderBy: 'order',
              expand: {
                processPortalMenuGroup: { select: 'title' },
                parent: { select: 'title' },
                customList: { select: 'name' },
                dynamicEntity: { select: 'displayName' },
                report: { select: 'title' },
                process: { select: 'name' },
              },
            },
          },
        ];

        const tabsToLoad = [];
        for (const config of menuConfigs) {
          const cacheKey = `${id}_${config.type}`;
          if (currentState.menuCache[cacheKey] === undefined) {
            tabsToLoad.push(config);
          }
        }

        if (tabsToLoad.length > 0) {
          for (const config of tabsToLoad) {
            const cacheKey = `${id}_${config.type}`;

            // Fetch all paginated data
            let allApiData = [];
            let page = 1;
            const limit = 100; // Fetch in chunks
            let hasMore = true;

            while (hasMore) {
              const apiResponse = yield call(api.getAll, {
                route: config.route,
                params: {
                  ...config.params,
                  page,
                  limit,
                },
              });

              // Handle paginated response (OData format: { value: [...] } or standard: { items: [...] })
              const pageData = apiResponse?.items || apiResponse || [];
              allApiData = [...allApiData, ...pageData];

              // Check if there are more pages
              const totalCount = apiResponse?.totalCount || apiResponse?.count || 0;
              hasMore = pageData.length === limit && allApiData.length < totalCount;
              page++;
            }

            const processedData = allApiData.map((item) => ({
              ...item,
              name:
                item.name || item.title || item.fullName || item.displayName || item.description,
            }));

            yield put({
              type: 'cacheMenuData',
              payload: {
                key: cacheKey,
                data: processedData,
              },
            });

            const currentLocalState = yield select(
              (state) => state.processPortalConfig[config.name],
            );
            if (
              currentLocalState === null ||
              (Array.isArray(currentLocalState) && currentLocalState.length === 0)
            ) {
              yield put({
                type: 'queryListMenu',
                payload: {
                  name: config.name,
                  data: processedData,
                },
              });
            }
          }
        }

        const updatedState = yield select((state) => state.processPortalConfig);
        const remainingGroupIds = new Set(
          (updatedState.listMenuGroups || []).map((group) => group.id),
        );

        const saveData = {
          groups: (updatedState.listMenuGroups || []).map((l) => ({
            id: l.id,
            title: l.title,
            order: l.order,
          })),
          items: (updatedState.listMenuItems || [])
            .filter((l) => {
              const menuGroupId = l.processPortalMenuGroupId ?? l.groupId ?? l.group?.id ?? null;
              return menuGroupId == null || remainingGroupIds.has(menuGroupId);
            })
            .map((l) => {
              const { group: _drop, processPortalMenuGroupId, groupId: formGroupId, ...rest } = l;
              const menuGroupId = processPortalMenuGroupId ?? formGroupId;
              return Object.fromEntries(
                Object.entries({
                  ...rest,
                  ...(menuGroupId != null ? { groupId: menuGroupId } : {}),
                }).filter(([key, value]) => value != null && !MENU_EXPAND_KEYS.includes(key)),
              );
            }),
        };

        yield call(api.exec, {
          route: `processPortal/updateMenu/${id}`,
          httpMethod: 'post',
          data: saveData,
        });

        const menuLists = {
          listMenuGroups: 'group',
          listMenuItems: 'item',
        };

        for (const listName of Object.keys(menuLists)) {
          const menuType = menuLists[listName];
          const cacheKey = `${id}_${menuType}`;
          const currentList = updatedState[listName] || [];
          const processedData = currentList.map((item) => ({
            ...item,
            name: item.name || item.title || item.fullName || item.displayName || item.description,
          }));
          yield put({
            type: 'cacheMenuData',
            payload: { key: cacheKey, data: processedData },
          });
        }

        // Close the menu section after saving (same pattern as permissions / relation sections).
        yield put({
          type: 'toggleConfigDetail',
          payload: { id: null, state: false, idProcessPortal: id },
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: false,
              visibleForm: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },
  },

  reducers: {
    saveSuccess(state) {
      return {
        ...state,
      };
    },
    queryToggleConfigDetail(state, { payload }) {
      const { id, state: isOpenConfig, dataTable } = payload;

      // If closing permissions config, clear permission lists
      if (!isOpenConfig && id === ConfigPermission.id) {
        return {
          ...state,
          isOpenConfig,
          idConfig: id,
          dataTable: isOpenConfig ? dataTable : [], // Clear dataTable when closing
          userListPermission: null, // null means never loaded
          positionListPermission: null,
          userGroupListPermission: null,
          areaListPermission: null,
        };
      }

      // If closing menu config, clear menu lists
      if (!isOpenConfig && id === ConfigMenu.id) {
        return {
          ...state,
          isOpenConfig,
          idConfig: id,
          dataTable: isOpenConfig ? dataTable : [],
          listMenuGroups: null,
          listMenuItems: null,
        };
      }

      // If opening a different configuration (not permissions or menu), clear permission and menu lists
      // This ensures that when switching from permissions/menu to another config, local changes are discarded
      if (isOpenConfig && id !== ConfigPermission.id && id !== ConfigMenu.id) {
        return {
          ...state,
          isOpenConfig,
          idConfig: id,
          dataTable: isOpenConfig ? dataTable : [], // Clear dataTable when closing
          userListPermission: null, // null means never loaded
          positionListPermission: null,
          userGroupListPermission: null,
          areaListPermission: null,
          listMenuGroups: null,
          listMenuItems: null,
        };
      }

      // If opening menu config from another config, clear permission lists
      if (isOpenConfig && id === ConfigMenu.id) {
        return {
          ...state,
          isOpenConfig,
          idConfig: id,
          dataTable: isOpenConfig ? dataTable : [],
          userListPermission: null,
          positionListPermission: null,
          userGroupListPermission: null,
          areaListPermission: null,
        };
      }

      // If opening permissions config from another config, clear menu lists
      if (isOpenConfig && id === ConfigPermission.id) {
        return {
          ...state,
          isOpenConfig,
          idConfig: id,
          dataTable: isOpenConfig ? dataTable : [],
          listMenuGroups: null,
          listMenuItems: null,
        };
      }

      return {
        ...state,
        isOpenConfig,
        idConfig: id,
        dataTable: isOpenConfig ? dataTable : [], // Clear dataTable when closing
      };
    },
    cacheConfigData(state, { payload }) {
      const { key, data } = payload;
      return {
        ...state,
        configCache: {
          ...state.configCache,
          [key]: data,
        },
      };
    },
    clearConfigCache(state) {
      return {
        ...state,
        configCache: {},
        permissionsCache: {}, // Clear permissions cache
        menuCache: {}, // Clear menu cache
        isOpenConfig: false,
        idConfig: null,
        dataTable: [],
        // Clear permission and menu lists when closing the entire configuration view
        userListPermission: null,
        positionListPermission: null,
        userGroupListPermission: null,
        areaListPermission: null,
        fetchingPermissionList: null,
        listMenuGroups: null,
        listMenuItems: null,
      };
    },
    queryToggleAddToConfig(state, { payload }) {
      return {
        ...state,
        isOpenFormAddConfig: payload.state,
      };
    },
    hideForms(state) {
      return {
        ...state,
        isOpenFormAddConfig: false,
      };
    },
    queryAddConfigProcessPortal(state, { payload }) {
      return {
        ...state,
        isOpenFormAddConfig: payload.state,
        idConfig: payload.id,
      };
    },
    queryFetchList(state, { payload }) {
      const { id, dataTable } = payload;
      return {
        ...state,
        idConfig: id,
        dataTable: dataTable.map((item) => ({
          id: item.id,
          name: item.name || item.title || item.fullName || item.displayName || item.description,
          description: item.description,
        })),
      };
    },
    addConfigItemList(state, { payload }) {
      const newDataTable = [...state.dataTable, payload];
      return {
        ...state,
        dataTable: newDataTable,
      };
    },
    deleteConfigItemList(state, { payload }) {
      const newDataTable = state.dataTable.filter((item) => item.id !== payload);
      return {
        ...state,
        dataTable: newDataTable,
      };
    },
    queryListPermissions(state, { payload }) {
      return {
        ...state,
        userListPermission: payload.userListPermission ?? state.userListPermission,
        positionListPermission: payload.positionListPermission ?? state.positionListPermission,
        userGroupListPermission: payload.userGroupListPermission ?? state.userGroupListPermission,
        areaListPermission: payload.areaListPermission ?? state.areaListPermission,
      };
    },
    queryAddListPermission(state, { payload }) {
      const { name, data } = payload;
      const currentList = state[name] === null ? [] : state[name] || [];
      return {
        ...state,
        [name]: [...currentList, data],
      };
    },
    queryDeleteItemPermission(state, { payload }) {
      const { name, id } = payload;
      const currentList = state[name] === null ? [] : state[name] || [];
      return {
        ...state,
        [name]: currentList.filter((item) => item.id !== id),
      };
    },
    queryFetchingPermissionList(state, { payload }) {
      return {
        ...state,
        fetchingPermissionList: payload ?? null,
      };
    },
    cachePermissionsData(state, { payload }) {
      const { key, data } = payload;
      return {
        ...state,
        permissionsCache: {
          ...state.permissionsCache,
          [key]: data,
        },
      };
    },
    queryListMenu(state, { payload }) {
      const { name, data, pagination, paginationKey } = payload;
      const normalized = Array.isArray(data)
        ? data.map((item) => ({ ...item, order: item?.order ?? 0 }))
        : data;
      return {
        ...state,
        [name]: normalized,
        ...(pagination &&
          paginationKey && {
            [paginationKey]: pagination,
          }),
      };
    },
    queryAddListMenu(state, { payload }) {
      const { name, data } = payload;
      const currentList = state[name] === null ? [] : state[name] || [];
      const nextList = [...currentList, { ...data, order: data?.order ?? 0 }];
      nextList.sort(compareMenuByOrder);
      return {
        ...state,
        [name]: nextList,
      };
    },
    queryUpdateListMenu(state, { payload }) {
      const { name, data } = payload;
      const normalized = { ...data, order: data?.order ?? 0 };
      const currentList = state[name] === null ? [] : state[name] || [];
      const updatedList = currentList
        .map((item) => (item.id === normalized.id ? normalized : item))
        .sort(compareMenuByOrder);

      if (name === 'listMenuGroups') {
        const updatedItems = Array.isArray(state.listMenuItems)
          ? state.listMenuItems.map((item) =>
              item.processPortalMenuGroupId === normalized.id
                ? {
                    ...item,
                    group: { ...item.group, id: normalized.id, title: normalized.title },
                  }
                : item,
            )
          : state.listMenuItems;

        return {
          ...state,
          [name]: updatedList,
          listMenuItems: updatedItems,
        };
      }

      return {
        ...state,
        [name]: updatedList,
      };
    },
    queryDeleteItemMenu(state, { payload }) {
      const { name, id } = payload;
      const currentList = state[name] === null ? [] : state[name] || [];
      const nextList = currentList.filter((item) => item.id !== id);

      // Deleting a group also removes its menu items from local state (saved together on saveMenu).
      if (name === 'listMenuGroups' && Array.isArray(state.listMenuItems)) {
        const belongsToGroup = (item) => {
          const groupId = item.processPortalMenuGroupId || item.group?.id || item.groupId || null;
          return groupId === id;
        };

        return {
          ...state,
          [name]: nextList,
          listMenuItems: state.listMenuItems.filter((item) => !belongsToGroup(item)),
        };
      }

      return {
        ...state,
        [name]: nextList,
      };
    },
    cacheMenuData(state, { payload }) {
      const { key, data } = payload;
      return {
        ...state,
        menuCache: {
          ...state.menuCache,
          [key]: data,
        },
      };
    },
  },
};

export default Model;
